import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../pages-styles/playerDetail.css';
import { usePageMeta } from '../hooks/usePageMeta';
import { mockPlayers } from '../data/mockPlayers';

function PlayerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  usePageMeta(
    `Player Profile - MAGBITY AGENCY LTD`,
    `View detailed player profile, statistics, and highlight videos.`
  );

  useEffect(() => {
    // Load player from mock data
    const loadPlayer = () => {
      try {
        const foundPlayer = mockPlayers.find(p => p.id === parseInt(id));
        if (!foundPlayer) {
          throw new Error('Player not found');
        }
        setPlayer(foundPlayer);
        setError(null);
      } catch (err) {
        console.error('Error loading player:', err);
        setError('Failed to load player details');
      } finally {
        setLoading(false);
      }
    };

    // Simulate network delay
    setTimeout(loadPlayer, 300);
  }, [id]);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '/default-player.jpg';
    // If it already starts with /, it's a local public file
    if (imageUrl.startsWith('/')) return imageUrl;
    // If it's a full URL, use it as-is
    if (imageUrl.startsWith('http')) return imageUrl;
    // Otherwise, assume it's a backend media URL
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    return `${apiUrl}${imageUrl}`;
  };

  const getVideoUrl = (videoUrl) => {
    if (!videoUrl) return null;
    if (videoUrl.startsWith('http')) return videoUrl;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    return `${apiUrl}${videoUrl}`;
  };

  const getHighlightVideos = () => {
    if (!player) return [];
    const videos = [];
    for (let i = 1; i <= 5; i++) {
      const video = player[`highlight_video_${i}`];
      if (video) {
        videos.push({ id: i, url: getVideoUrl(video) });
      }
    }
    return videos;
  };

  const highlightVideos = player ? getHighlightVideos() : [];

  if (loading) {
    return (
      <div className="player-detail-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading player details...</p>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="player-detail-page">
        <div className="error-container">
          <p>{error || 'Player not found'}</p>
          <button onClick={() => navigate('/')} className="back-btn">Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="player-detail-page">
      {/* Back Button */}
      <button onClick={() => navigate('/')} className="back-button">
        ← Back
      </button>

      {/* Hero Section with Profile Image */}
      <section className="player-hero">
        <div className="hero-background"></div>
        <div className="hero-content">
          <div className="profile-image-container">
            <img 
              src={getImageUrl(player.profile_picture)} 
              alt={`${player.first_name} ${player.last_name}`}
              className="profile-image"
              onError={(e) => {
                e.target.src = '/default-player.jpg';
              }}
            />
          </div>
          <div className="profile-header">
            <h1>{player.first_name} {player.last_name}</h1>
            <div className="profile-badges">
              {player.position && (
                <span className="badge position-badge">{player.position}</span>
              )}
              {player.nationality?.name && (
                <span className="badge nationality-badge">{player.nationality.name}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="detail-container">
        {/* Left Column - Basic Info */}
        <aside className="sidebar">
          <div className="info-section">
            <h3>Personal Info</h3>
            <div className="info-grid">
              {player.date_of_birth && (
                <div className="info-item">
                  <label>Date of Birth</label>
                  <p>{new Date(player.date_of_birth).toLocaleDateString()}</p>
                </div>
              )}
              {player.height_cm && (
                <div className="info-item">
                  <label>Height</label>
                  <p>{player.height_cm} cm</p>
                </div>
              )}
              {player.weight_kg && (
                <div className="info-item">
                  <label>Weight</label>
                  <p>{player.weight_kg} kg</p>
                </div>
              )}
              {player.dominant_foot && (
                <div className="info-item">
                  <label>Dominant Foot</label>
                  <p>{player.dominant_foot}</p>
                </div>
              )}
            </div>
          </div>

          <div className="info-section">
            <h3>Club Info</h3>
            <div className="info-item">
              <label>Current Club</label>
              <p>{player.current_club?.name || 'Not Assigned'}</p>
            </div>
            {player.wing?.name && (
              <div className="info-item">
                <label>Wing</label>
                <p>{player.wing.name}</p>
              </div>
            )}
            {player.contract_start && (
              <div className="info-item">
                <label>Contract Start</label>
                <p>{new Date(player.contract_start).toLocaleDateString()}</p>
              </div>
            )}
            {player.contract_end && (
              <div className="info-item">
                <label>Contract End</label>
                <p>{new Date(player.contract_end).toLocaleDateString()}</p>
              </div>
            )}
          </div>

          <div className="info-section">
            <h3>Market Value</h3>
            <p className="market-value">${player.market_value_usd?.toLocaleString() || '0'}</p>
          </div>
        </aside>

        {/* Right Column - Statistics & Videos */}
        <main className="main-content">
          {/* Statistics */}
          <section className="stats-section">
            <h2>Career Statistics</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-number">{player.appearances || 0}</div>
                <div className="stat-label">Appearances</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{player.goals || 0}</div>
                <div className="stat-label">Goals</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{player.assists || 0}</div>
                <div className="stat-label">Assists</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{player.yellow_cards || 0}</div>
                <div className="stat-label">Yellow Cards</div>
              </div>
              <div className="stat-card">
                <div className="stat-number">{player.red_cards || 0}</div>
                <div className="stat-label">Red Cards</div>
              </div>
            </div>
          </section>

          {/* Videos Section */}
          {highlightVideos.length > 0 && (
            <section className="videos-section">
              <h2>Highlight Videos</h2>
              
              {/* Main Video Player */}
              <div className="main-video-container">
                <div className="video-player">
                  <video 
                    key={highlightVideos[selectedVideoIndex]?.url}
                    controls
                    autoPlay
                    style={{ width: '100%', height: '100%' }}
                  >
                    <source src={highlightVideos[selectedVideoIndex]?.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>

              {/* Video Thumbnails */}
              {highlightVideos.length > 1 && (
                <div className="video-thumbnails">
                  {highlightVideos.map((video, index) => (
                    <button 
                      key={index}
                      className={`video-thumbnail ${selectedVideoIndex === index ? 'active' : ''}`}
                      onClick={() => setSelectedVideoIndex(index)}
                      title={`Video ${index + 1}`}
                    >
                      <span>Video {index + 1}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Additional Info */}
          {player.previous_clubs && (
            <section className="previous-clubs-section">
              <h2>Previous Clubs</h2>
              <p className="previous-clubs-text">{player.previous_clubs}</p>
            </section>
          )}
        </main>
      </div>

      {/* Contact CTA */}
      <section className="contact-cta-section">
        <div className="cta-container">
          <h2>Interested in this player?</h2>
          <p>Get in touch with our team to discuss representation, transfers, or partnerships.</p>
          <a href="/contact" className="cta-button">Contact Us</a>
        </div>
      </section>
    </div>
  );
}

export default PlayerDetail;
